import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Tier1SleeveRow } from '../../types/tier1Portfolio';
import EtfSymbolLink from './EtfSymbolLink';
import FillStatusBadge from './FillStatusBadge';
import {
  formatRegistryStatus,
  PLANNED_ACTION_CLASS,
  PLANNED_ACTION_LABEL,
} from './tier1PlannedAction';
import { accountDetailPath } from '../../utils/appRoutes';
import { fmtPct, fmtUsd, fmtUsdPrecise } from './format';

interface Props {
  sleeves: Tier1SleeveRow[];
  accountId: string;
  /** Bar chart label for target weights (default Policy). */
  policyLabel?: string;
  rationaleBySleeve?: Record<string, string>;
  onSleeveClick?: (sleeveKey: string) => void;
  /** Pending plan preview: show Neutral + Cockpit before New plan. */
  showMacroBaselines?: boolean;
}

const WeightBar: React.FC<{
  policy: number;
  actual: number;
  cockpit?: number;
  neutral?: number;
  currentPolicy?: number;
  policyLabel: string;
  showMacroBaselines?: boolean;
}> = ({
  policy,
  actual,
  cockpit,
  neutral,
  currentPolicy,
  policyLabel,
  showMacroBaselines,
}) => {
  const max = Math.max(
    policy,
    actual,
    cockpit ?? 0,
    neutral ?? 0,
    currentPolicy ?? 0,
    1,
  );
  return (
    <div className="space-y-1">
      {showMacroBaselines && neutral != null ? (
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span className="w-12 shrink-0 truncate" title="Macro neutral baseline">
            Neutral
          </span>
          <div className="relative h-2 flex-1 rounded bg-gray-100">
            <div
              className="absolute inset-y-0 left-0 rounded bg-gray-300"
              style={{ width: `${(neutral / max) * 100}%` }}
            />
          </div>
          <span className="w-12 text-right">{fmtPct(neutral)}</span>
        </div>
      ) : null}
      {showMacroBaselines && cockpit != null ? (
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span className="w-12 shrink-0 truncate" title="Live macro cockpit target">
            Cockpit
          </span>
          <div className="relative h-2 flex-1 rounded bg-gray-100">
            <div
              className="absolute inset-y-0 left-0 rounded bg-violet-400"
              style={{ width: `${(cockpit / max) * 100}%` }}
            />
          </div>
          <span className="w-12 text-right">{fmtPct(cockpit)}</span>
        </div>
      ) : null}
      {currentPolicy != null ? (
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span className="w-12 shrink-0 truncate" title="Current active IPS">
            Current
          </span>
          <div className="relative h-2 flex-1 rounded bg-gray-100">
            <div
              className="absolute inset-y-0 left-0 rounded bg-amber-200"
              style={{ width: `${(currentPolicy / max) * 100}%` }}
            />
          </div>
          <span className="w-12 text-right">{fmtPct(currentPolicy)}</span>
        </div>
      ) : null}
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <span className="w-12 shrink-0 truncate" title={policyLabel}>
          {policyLabel}
        </span>
        <div className="relative h-2 flex-1 rounded bg-gray-100">
          <div
            className="absolute inset-y-0 left-0 rounded bg-sky-200"
            style={{ width: `${(policy / max) * 100}%` }}
          />
        </div>
        <span className="w-12 text-right">{fmtPct(policy)}</span>
      </div>
      {!showMacroBaselines && cockpit != null ? (
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span className="w-12 shrink-0 truncate" title="Live macro cockpit">
            Cockpit
          </span>
          <div className="relative h-2 flex-1 rounded bg-gray-100">
            <div
              className="absolute inset-y-0 left-0 rounded bg-violet-400"
              style={{ width: `${(cockpit / max) * 100}%` }}
            />
          </div>
          <span className="w-12 text-right">{fmtPct(cockpit)}</span>
        </div>
      ) : null}
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <span className="w-12">Actual</span>
        <div className="relative h-2 flex-1 rounded bg-gray-100">
          <div
            className="absolute inset-y-0 left-0 rounded bg-sky-600"
            style={{ width: `${(actual / max) * 100}%` }}
          />
        </div>
        <span className="w-12 text-right">{fmtPct(actual)}</span>
      </div>
    </div>
  );
};

const Tier1SleeveGrid: React.FC<Props> = ({
  sleeves,
  accountId,
  policyLabel = 'Policy',
  rationaleBySleeve,
  onSleeveClick,
  showMacroBaselines,
}) => {
  const sorted = useMemo(
    () =>
      [...sleeves].sort(
        (a, b) => (b.needsAdditionalUsd ?? 0) - (a.needsAdditionalUsd ?? 0),
      ),
    [sleeves],
  );

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      {sorted.map((s) => {
        const holding = s.holdings[0];
        const symbol = s.intendedSymbol ?? holding?.symbol ?? '—';
        const mv = holding?.marketValue ?? s.actualNotionalUsd ?? 0;
        const qty = holding?.quantity;
        const fill = s.fillStatus ?? 'empty';

        const planned = s.plannedAction;
        const clickable = !!onSleeveClick && planned === 'pending_approval';

        return (
          <div
            key={s.sleeveKey}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={clickable ? () => onSleeveClick?.(s.sleeveKey) : undefined}
            onKeyDown={
              clickable
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') onSleeveClick?.(s.sleeveKey);
                  }
                : undefined
            }
            className={`rounded-lg border border-gray-200 bg-white p-4 shadow-sm ${
              clickable ? 'cursor-pointer hover:border-violet-300 hover:ring-1 hover:ring-violet-200' : ''
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-semibold text-gray-900">{s.name ?? s.sleeveKey}</h3>
                <p className="text-sm text-gray-600">
                  <EtfSymbolLink symbol={symbol} sleeveKey={s.sleeveKey} />
                  {s.tagVerified ? (
                    <span className="ml-2 text-xs text-green-700">Registered</span>
                  ) : null}
                </p>
                {s.registryStatus ? (
                  <p className="mt-0.5 text-xs text-gray-500">
                    Slot: {formatRegistryStatus(s.registryStatus)}
                  </p>
                ) : null}
              </div>
              <div className="flex flex-col items-end gap-1">
                {s.fillStatus ? (
                  <FillStatusBadge status={fill} needsAdditionalUsd={s.needsAdditionalUsd} />
                ) : null}
                {s.attention && s.attention !== 'filled' ? (
                  <span className="inline-flex rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-700">
                    {s.attention.replace(/_/g, ' ')}
                  </span>
                ) : null}
                {planned && planned !== 'hold' ? (
                  <span
                    className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${PLANNED_ACTION_CLASS[planned]}`}
                  >
                    {PLANNED_ACTION_LABEL[planned]}
                  </span>
                ) : null}
              </div>
            </div>

            <div className="mt-3">
              <WeightBar
                policy={s.policyWeightPct}
                actual={s.actualWeightPct}
                cockpit={s.cockpitWeightPct}
                neutral={s.neutralWeightPct}
                currentPolicy={s.currentPolicyWeightPct}
                policyLabel={policyLabel}
                showMacroBaselines={showMacroBaselines}
              />
              {showMacroBaselines &&
              s.cockpitWeightPct != null &&
              Math.abs(s.policyWeightPct - s.cockpitWeightPct) > 0.05 ? (
                <p className="mt-1 text-xs font-medium text-violet-800">
                  Account AI {s.policyWeightPct > s.cockpitWeightPct ? '+' : ''}
                  {(s.policyWeightPct - s.cockpitWeightPct).toFixed(1)} pts vs cockpit
                </p>
              ) : null}
            </div>
            {rationaleBySleeve?.[s.sleeveKey] ? (
              <p className="mt-2 text-xs leading-relaxed text-gray-600">
                {rationaleBySleeve[s.sleeveKey]}
              </p>
            ) : null}

            <div className="mt-3 space-y-1 text-sm text-gray-700">
              {s.policyNotionalUsd != null ? (
                <p>
                  Target {fmtUsd(s.policyNotionalUsd)}
                  {s.actualNotionalUsd != null ? ` · Held ${fmtUsd(s.actualNotionalUsd)}` : ''}
                </p>
              ) : null}
              {s.attention === 'untradeable_residual' ? (
                <p className="text-gray-600">{s.attentionReason ?? 'Below one whole share'}</p>
              ) : fill !== 'filled' && (s.needsAdditionalUsd ?? 0) > 0 ? (
                <p className="font-medium text-amber-800">
                  Need {fmtUsdPrecise(s.needsAdditionalUsd!)} to policy
                </p>
              ) : null}
              {s.attention &&
              s.attention !== 'filled' &&
              s.attention !== 'untradeable_residual' &&
              s.attentionReason ? (
                <p className="text-gray-600">{s.attentionReason}</p>
              ) : null}
              <p className="text-gray-500">
                {mv > 0 ? `${fmtUsd(mv)} deployed` : 'No Schwab fill yet'}
                {qty != null && qty > 0 ? ` · ${qty} sh` : ''}
              </p>
            </div>

            {s.positionId ? (
              <Link
                to={accountDetailPath(accountId)}
                state={{ initialTab: 'positions', positionId: s.positionId }}
                className="mt-2 inline-block text-xs text-primary-600 hover:underline"
              >
                Registry {s.positionId.slice(-8)}
              </Link>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

export default Tier1SleeveGrid;
